import { Clock } from "three";
import { Howl } from "howler";
import start_wav from "@/assets/audio/start.wav";
import end_wav from "@/assets/audio/end.wav";

const STATES = {
  BLANK: 0,
  IDLE: 1,
  LOADING: 2,
  SPEAKING: 3,
  LISTENING: 4,
  MESSAGE: 5,
};

const TRANSITIONS = {
  INIT: {
    from: STATES.BLANK,
    to: STATES.LOADING,
    duration: 1.0,
    id: 0,
    sound: start_wav,
  },
  SPIRAL: {
    from: STATES.LOADING,
    to: STATES.SPEAKING,
    duration: 0.8,
    id: 1,
  },
  SHRINK: {
    from: STATES.SPEAKING,
    to: STATES.IDLE,
    duration: 0.5,
    id: 2,
  },
  SPLIT: {
    from: STATES.LISTENING,
    to: STATES.LOADING,
    duration: 1.0,
    id: 3,
  },
  LISTEN: {
    from: STATES.IDLE,
    to: STATES.LISTENING,
    duration: 0.5,
    id: 4,
  },
  IDLE_TO_MESSAGE: {
    from: STATES.IDLE,
    to: STATES.MESSAGE,
    duration: 0.7,
    id: 5,
  },
  MESSAGE_TO_IDLE: {
    from: STATES.MESSAGE,
    to: STATES.IDLE,
    duration: 0.4,
    id: 6,
  },
};

class AnimationController {
  constructor(material, initialScreen = "INTRO", initialState = STATES.BLANK) {
    this.material = material;
    this.clock = new Clock();
    this.sounds = {};
    this.endSound = null;
    this._transitionTimeout = null;
    this.isDisposed = false;
    this.initSounds();
    this.currentScreen = initialScreen;

    this.state = {
      value: initialState,
      start: 0,
    };
    this.transition = null;
    this.call("INIT");

    window.intro_audio_played = false;
  }

  changeScreen(screen) {
    if (this.isDisposed) return;
    this.currentScreen = screen;
  }

  initSounds() {
    Object.entries(TRANSITIONS).forEach(([key, transition]) => {
      if (transition.sound) {
        this.sounds[key] = new Howl({
          src: [transition.sound],
          volume: 1.0,
          preload: true,
        });
      }
    });
    this.endSound = new Howl({
      src: [end_wav],
      volume: 1.0,
      preload: true,
    });
  }

  forceState(stateValue) {
    if (this.isDisposed) return;
    if (this.transition) {
      clearTimeout(this._transitionTimeout);
      this._transitionTimeout = null;
      this.transition = null;
    }
    this.state = {
      value: stateValue,
      start: this.clock.getElapsedTime(),
    };
  }

  forceListening() {
    this.forceState(STATES.LISTENING);
  }

  forceMessage() {
    this.forceState(STATES.MESSAGE);
  }

  // Cancels IDLE_TO_MESSAGE mid-transition or triggers MESSAGE_TO_IDLE if already in MESSAGE.
  // Safe to call at any time — ignored if blob is in an unrelated state.
  exitMessageState() {
    if (this.isDisposed) return;
    const midTransition =
      this.transition?.id === TRANSITIONS.IDLE_TO_MESSAGE.id;
    if (midTransition) {
      clearTimeout(this._transitionTimeout);
      this._transitionTimeout = null;
      this.transition = null;
      this.state = { value: STATES.IDLE, start: this.clock.getElapsedTime() };
    } else {
      this.call("MESSAGE_TO_IDLE");
    }
  }

  call(transition, { force = false } = {}) {
    if (this.isDisposed) return;
    const transitionConfig = TRANSITIONS[transition];
    if (transitionConfig) {
      if (!force && transitionConfig.from !== this.state?.value) {
        return;
      }
      if (this.transition) {
        clearTimeout(this._transitionTimeout);
        this._transitionTimeout = null;
        this.transition = null;
      }

      this.state = null;
      this.transition = {
        id: transitionConfig.id,
        duration: transitionConfig.duration,
        start: this.clock.getElapsedTime(),
      };

      if (
        this.sounds[transition] &&
        this.currentScreen === "INTRO" &&
        !window.intro_audio_played
      ) {
        this.sounds[transition].play();
        window.intro_audio_played = true;
      }

      this._transitionTimeout = setTimeout(() => {
        if (this.isDisposed) return;
        this.state = {
          value: transitionConfig.to,
          start: this.clock.getElapsedTime(),
        };
        this.transition = null;
        this._transitionTimeout = null;
      }, transitionConfig.duration * 1000);
    }
  }

  update() {
    if (this.isDisposed || !this.material?.uniforms) return;
    if (this.transition != null) {
      this.material.uniforms.u_state.value = -1;
      this.material.uniforms.u_state_start.value = 0;
      this.material.uniforms.u_transition_type.value = this.transition.id;
      this.material.uniforms.u_transition_start.value = this.transition.start;
      this.material.uniforms.u_duration.value = this.transition.duration;
    } else {
      this.material.uniforms.u_state.value = this.state.value;
      this.material.uniforms.u_state_start.value = this.state.start;
      this.material.uniforms.u_transition_type.value = -1;
      this.material.uniforms.u_transition_start.value = 0;
      this.material.uniforms.u_duration.value = 0;
    }
  }

  end() {
    if (this.isDisposed || !this.material?.uniforms) return;
    this.material.uniforms.u_stop_session.value = true;
    this.material.uniforms.u_stop_time.value = this.clock.getElapsedTime();
    this.endSound?.play();
    Object.values(this.sounds).forEach((sound) => {
      if (sound.playing()) {
        sound.stop();
      }
    });
  }

  dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;

    if (this._transitionTimeout) {
      clearTimeout(this._transitionTimeout);
      this._transitionTimeout = null;
    }

    Object.values(this.sounds).forEach((sound) => {
      try {
        if (sound.playing()) {
          sound.stop();
        }
        sound.unload();
      } catch {
        // Howler cleanup is best-effort during React teardown.
      }
    });
    this.sounds = {};

    if (this.endSound) {
      try {
        if (this.endSound.playing()) {
          this.endSound.stop();
        }
        this.endSound.unload();
      } catch {
        // Howler cleanup is best-effort during React teardown.
      }
      this.endSound = null;
    }

    this.transition = null;
    this.material = null;
  }
}

export default AnimationController;
